import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * DSN should be set via VITE_SENTRY_DSN environment variable
 */
let sentryInitialized = false;

export function initSentry() {
  // Idempotency guard: Sentry Session Replay only supports one instance per page.
  // main.tsx can be re-evaluated (HMR, SW update, duplicated module copies).
  const globalScope = window as any;
  if (sentryInitialized || globalScope.__divus_sentry_initialized__ || Sentry.getClient()) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
  const isProduction = environment === 'production';
  
  // Skip initialization if no DSN is configured
  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking disabled.');
    return;
  }

  sentryInitialized = true;
  globalScope.__divus_sentry_initialized__ = true;


  Sentry.init({
    dsn,
    release: release || undefined,
    environment,
    
    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      
      // React Router v6 integration
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      
      // Session Replay for debugging
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Conservative production sampling while preserving full error visibility.
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    
    // Trace propagation targets for distributed tracing
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/divus\.app/,
      /^https:\/\/app\.divuslegalgroup\.com/,
      /^https:\/\/.*\.supabase\.co/,
    ],
    
    // Session Replay sampling
    replaysSessionSampleRate: isProduction ? 0.02 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Enable debug in development
    debug: import.meta.env.DEV,
    
    // Before sending, filter non-fatal errors and log in development
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Filter "Non-Error promise rejection captured with value: undefined"
      // Caused by Twilio SDK rejecting promises with undefined on disconnect
      if (error === undefined || error === null) {
        return null;
      }
      if (typeof error === 'string' && error.includes('Non-Error promise rejection captured with value')) {
        return null;
      }
      
      // Filter AbortError from audio.play() - Twilio SDK expected behavior
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null;
      }
      
      // Filter out Radix UI Portal removeChild errors - not real errors
      if (error instanceof DOMException && error.message?.includes('removeChild')) {
        return null;
      }
      // Also catch removeChild errors that aren't DOMException instances
      if (error instanceof Error && error.message?.includes('removeChild')) {
        return null;
      }
      // Filter Safari NotFoundError - same DOM error, different message
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return null;
      }
      if (error instanceof Error && error.message?.includes('The object can not be found here')) {
        return null;
      }
      // Filtra corrida interna do SDK Twilio: replaceTrack em peer connection fechada
      if (
        (error instanceof DOMException || error instanceof Error) &&
        (error.name === 'InvalidStateError' ||
          error.message?.includes('replaceTrack') ||
          error.message?.includes('peer connection is closed'))
      ) {
        return null;
      }
      // Filtra setSinkId do SDK Twilio sem gesto do usuário (áudio cai no alto-falante padrão)
      if (
        (error instanceof DOMException || error instanceof Error) &&
        error.name === 'NotAllowedError' &&
        (error.message?.includes('user gesture') || error.stack?.includes('setSinkId'))
      ) {
        return null;
      }
      if (event.exception?.values?.some(v =>
        v.type === 'NotAllowedError' &&
        (v.value?.includes('user gesture') ||
          v.stacktrace?.frames?.some(f =>
            f.function?.includes('setSinkId') || f.filename?.includes('peerconnection.js')
          ))
      )) {
        return null;
      }
      // Filtra avisos esperados de fluxo de chamada (não são erros reais)
      if (error instanceof Error && (
        error.message?.includes('Call already in progress') ||
        error.message?.includes('Microphone permission denied')
      )) {
        return null;
      }
      // Fallback: check event exception values directly
      if (event.exception?.values?.some(v =>
        v.value?.includes('removeChild') ||
        v.value?.includes('The object can not be found here') ||
        v.value?.includes('Call already in progress') ||
        v.value?.includes('Microphone permission denied') ||
        v.type === 'InvalidStateError' ||
        v.value?.includes('replaceTrack') ||
        v.value?.includes('peer connection is closed')
      )) {
        return null;
      }

      // --- SyntaxError de JSON: classificar origem e descartar ruído de rede ---
      const isJsonSyntaxError =
        (error instanceof SyntaxError && /JSON|Unexpected (token|end of)/i.test(error.message || '')) ||
        !!event.exception?.values?.some(
          v => v.type === 'SyntaxError' && /JSON|Unexpected (token|end of)/i.test(v.value || '')
        );

      if (isJsonSyntaxError) {
        const breadcrumbs = event.breadcrumbs || [];
        const recent = breadcrumbs.slice(-25);

        // Última requisição HTTP registrada (ajuda a achar o payload truncado)
        const lastFetch = [...recent].reverse().find(
          b => b.category === 'fetch' || b.category === 'xhr'
        );
        const lastFetchUrl = (lastFetch?.data as any)?.url as string | undefined;

        // Sinais de perda de conexão imediatamente antes do erro
        const networkDrop = recent.some(b => {
          const text = `${b.message || ''} ${JSON.stringify(b.data || {})}`;
          return (
            text.includes('close event code: 1006') ||
            text.includes('31005') ||
            text.includes('31009') ||
            text.includes('WebSocket is not open') ||
            text.includes('No transport available')
          );
        });

        if (networkDrop) {
          // Payload cortado por queda de conexão: transitório, não é bug da aplicação
          return null;
        }

        event.tags = {
          ...event.tags,
          json_parse_error: 'true',
          json_parse_origin: lastFetchUrl ? 'fetch' : 'unknown',
        };
        event.extra = {
          ...event.extra,
          last_fetch_url: lastFetchUrl || null,
        };
      }




      if (import.meta.env.DEV) {
        console.error('[Sentry] Captured error:', hint.originalException || hint.syntheticException);
      }
      return event;
    },
  });

  console.log('[Sentry] Initialized for environment:', environment, 'release:', release || 'auto');
}

/**
 * Set user context in Sentry when user logs in
 */
export function setSentryUser(user: { id: string; email?: string | null; display_name?: string | null } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email || undefined,
      username: user.display_name || undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category?: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category: category || 'app',
    data,
    level: 'info',
  });
}

// Re-export Sentry components
export { Sentry };
